import { useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import { DatePicker } from '@fluentui/react';
import { Menu, MenuItem, MenuList, MenuPopover, MenuTrigger } from '@fluentui/react-components';
import { Add20Regular, ArrowDownload20Regular, ArrowDownLeft20Regular, ArrowUpRight20Regular, ChevronDown16Regular, ReceiptMoney20Regular, Wallet20Regular } from '@fluentui/react-icons';

// functions
import * as ownFunctions from './Cashflow.General.Functions';
import {
    buildAging,
    buildKpis,
    buildMonthlySeries,
    counterpartOf,
    currenciesOf,
    DateRange,
    groupByCounterpart,
    initialsOf,
    isPending,
    PeriodPreset,
    plural,
    presetRange,
    settledDebts
} from './Cashflow.Dashboard.Functions';
import { cashflowSheets } from './Cashflow.Excel';
import { ALL_ROWS, DEFAULT_PAGE_SIZE, PAGE_SIZES, PARTIES_PREVIEW, clampPage, pageCount, pageRangeLabel, pageSlice } from './Cashflow.Paging';
import { buildFileName, downloadWorkbook } from '../../Contracts/statistics/StatisticsExcel.Functions';
import { formatCurrencyAmountWithIssue } from '../../../commons/currency/Currency.Functions';

// models
import { Debt } from '../../../models/Entities/CashFlow/Debt/Debt';
import { Payment } from '../../../models/Entities/CashFlow/Payment/Payment';

// components
import { AgingCard, MonthlyChart } from './Cashflow.Charts.Component';
import { DebtsTable, PaymentsTable } from './Cashflow.Table.Component';
import DebtFormComponent from '../Debts/Debt.Form/Debt.Form.Component';
import { FormType as DebtFormType } from '../Debts/Debt.Form/Debt.Form.Functions';
import DebtDetailsComponent from '../Debts/Debt.Details/Debt.Details.Component';
import PaymentFormComponent from '../Payments/Payment.Form/Payment.Form.Component';
import { FormType as PaymentFormType } from '../Payments/Payment.Form/Payment.Form.Function';
import PaymentImputationFormComponent from '../Payments/PaymentImputation.Form/PaymentImputation.Form.Component';
import PaymentImputationsViewComponent from '../Payments/PaymentImputations.View/PaymentImputations.View.Component';

// styles
import './Cashflow.General.Styles.css';

type WorkTab = 'receivable' | 'payable' | 'received' | 'done' | 'settled';

const PRESETS: { key: PeriodPreset; label: string }[] = [
    { key: 'month', label: 'Este mes' },
    { key: 'quarter', label: 'Trimestre' },
    { key: 'year', label: 'Este año' },
    { key: 'custom', label: 'Personalizado' }
];

const fmt = (amount: number, currency: string) => formatCurrencyAmountWithIssue(amount, currency);
const fmtDate = (date?: Date) => (date ? date.toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit', year: 'numeric' }) : '');

/**
 * La caja del centro en una pantalla: cómo está (indicadores), qué está
 * envejeciendo y con quién, y la mesa de trabajo para cobrar, pagar e imputar.
 */
const CashFlowGeneralComponent = (props: ownFunctions.Props) => {

    // ── Filtros ──
    const [preset, setPreset] = useState<PeriodPreset>('year');
    const [customRange, setCustomRange] = useState<DateRange>(() => presetRange('month'));
    const range: DateRange = preset === 'custom' ? customRange : presetRange(preset);

    const currencies = useMemo(
        () => currenciesOf([...props.DebtsAsCreditor, ...props.DebtsAsDebtor], [...props.PaymentsReceived, ...props.PaymentsDone]),
        [props.DebtsAsCreditor, props.DebtsAsDebtor, props.PaymentsReceived, props.PaymentsDone]
    );
    const [chosenCurrency, setChosenCurrency] = useState<string | undefined>(undefined);
    const currency = chosenCurrency && currencies.includes(chosenCurrency) ? chosenCurrency : (currencies[0] ?? 'ARS');

    const [tab, setTab] = useState<WorkTab>('receivable');
    const [counterpartFilter, setCounterpartFilter] = useState<string | undefined>(undefined);

    // ── Paneles ──
    const [debtForm, setDebtForm] = useState<{ open: boolean; type: DebtFormType; debtId?: number }>({ open: false, type: DebtFormType.Creditor });
    const [detailsDebtId, setDetailsDebtId] = useState<number | undefined>(undefined);
    const [paymentForm, setPaymentForm] = useState<{ open: boolean; type: PaymentFormType; paymentId?: number; debtId?: number }>({ open: false, type: PaymentFormType.receiver });
    const [imputationsOf, setImputationsOf] = useState<Payment | null>(null);
    const [imputing, setImputing] = useState<Payment | null>(null);
    const [exportState, setExportState] = useState<'idle' | 'busy' | 'failed'>('idle');

    // ── Datos ──
    useEffect(() => {
        // Las personas resuelven los nombres de las contrapartes. Sin filtro el
        // servicio trae 100 y las demás quedarían como identificador crudo.
        props.GetAllPersons({ PageNumber: 1, PageSize: 1000 });
        props.GetAllDebtsByCreditor(props.EntityIdentifier, props.FixedAssociatedEntityIdentifier);
        props.GetAllDebtsByDebtor(props.EntityIdentifier, props.FixedAssociatedEntityIdentifier);
        props.GetAllPaymentsByPayer(props.EntityIdentifier, props.FixedAssociatedEntityIdentifier);
        props.GetAllPaymentsByReceiver(props.EntityIdentifier, props.FixedAssociatedEntityIdentifier);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.EntityIdentifier, props.FixedAssociatedEntityIdentifier]);

    const kpis = useMemo(() => buildKpis({
        debtsAsCreditor: props.DebtsAsCreditor,
        debtsAsDebtor: props.DebtsAsDebtor,
        paymentsReceived: props.PaymentsReceived,
        paymentsDone: props.PaymentsDone,
        currency,
        range
    }), [props.DebtsAsCreditor, props.DebtsAsDebtor, props.PaymentsReceived, props.PaymentsDone, currency, range]);

    const monthly = useMemo(() => buildMonthlySeries({
        debtsAsCreditor: props.DebtsAsCreditor,
        paymentsReceived: props.PaymentsReceived,
        paymentsDone: props.PaymentsDone,
        currency,
        range
    }), [props.DebtsAsCreditor, props.PaymentsReceived, props.PaymentsDone, currency, range]);

    const aging = useMemo(() => buildAging(props.DebtsAsCreditor, currency), [props.DebtsAsCreditor, currency]);

    // Con quién: lo por cobrar (deudores) o lo por pagar (acreedores).
    const [partiesMode, setPartiesMode] = useState<'receivable' | 'payable'>('receivable');
    const [showAllParties, setShowAllParties] = useState(false);
    // Todas las contrapartes con saldo, no sólo las primeras: el que filtra
    // necesita encontrar la suya aunque deba poco.
    const allParties = useMemo(
        () => (partiesMode === 'receivable'
            ? groupByCounterpart(props.DebtsAsCreditor, currency, (debt) => debt.debtor, props.NameOf)
            : groupByCounterpart(props.DebtsAsDebtor, currency, (debt) => debt.creditor, props.NameOf)
        ),
        [partiesMode, props.DebtsAsCreditor, props.DebtsAsDebtor, currency, props.NameOf]
    );
    const parties = showAllParties ? allParties : allParties.slice(0, PARTIES_PREVIEW);
    const hiddenParties = allParties.length - parties.length;

    const pickParty = (identifier: string) => {
        const same = counterpartFilter === identifier;
        setCounterpartFilter(same ? undefined : identifier);
        if (!same) setTab(partiesMode);
    };

    // ── Filas de la mesa de trabajo ──
    const byCounterpart = <T,>(items: T[], counterpartOf: (item: T) => string): T[] =>
        counterpartFilter ? items.filter((item) => counterpartOf(item) === counterpartFilter) : items;

    const rows = {
        receivable: byCounterpart(props.DebtsAsCreditor.filter(isPending), (debt) => debt.debtor),
        payable: byCounterpart(props.DebtsAsDebtor.filter(isPending), (debt) => debt.creditor),
        // Saldadas en los dos sentidos: lo que nos pagaron y lo que pagamos.
        settled: byCounterpart(settledDebts(props.DebtsAsCreditor, props.DebtsAsDebtor), (debt) => counterpartOf(debt, props.EntityIdentifier)),
        received: byCounterpart(props.PaymentsReceived, (payment) => payment.payer),
        done: byCounterpart(props.PaymentsDone, (payment) => payment.receiver)
    };

    // ── Paginado de la mesa de trabajo (en el cliente: el API trae todo) ──
    const [pageSize, setPageSize] = useState<number>(DEFAULT_PAGE_SIZE);
    const [page, setPage] = useState(1);
    const totalRows = rows[tab].length;
    const currentPage = clampPage(page, totalRows, pageSize);
    const pages = pageCount(totalRows, pageSize);
    useEffect(() => { setPage(1); }, [tab, counterpartFilter, currency, pageSize]);

    const pager = (
        <div className="cf-pager" role="navigation" aria-label="Paginado">
            <span className="cf-pager__range">{pageRangeLabel(totalRows, currentPage, pageSize)}</span>
            <span className="cf-pager__sizes" role="group" aria-label="Filas por página">
                {PAGE_SIZES.map((size) => (
                    <button key={size} type="button" className={`cf-pager__size${pageSize === size ? ' is-active' : ''}`} onClick={() => setPageSize(size)}>{size}</button>
                ))}
                <button type="button" className={`cf-pager__size${pageSize === ALL_ROWS ? ' is-active' : ''}`} onClick={() => setPageSize(ALL_ROWS)}>Todas</button>
            </span>
            {pages > 1 && (
                <span className="cf-pager__nav">
                    <button type="button" className="cf-pager__btn" disabled={currentPage <= 1} onClick={() => setPage(1)} aria-label="Primera página">«</button>
                    <button type="button" className="cf-pager__btn" disabled={currentPage <= 1} onClick={() => setPage(currentPage - 1)} aria-label="Página anterior">‹</button>
                    <span className="cf-pager__page">{currentPage} / {pages}</span>
                    <button type="button" className="cf-pager__btn" disabled={currentPage >= pages} onClick={() => setPage(currentPage + 1)} aria-label="Página siguiente">›</button>
                    <button type="button" className="cf-pager__btn" disabled={currentPage >= pages} onClick={() => setPage(pages)} aria-label="Última página">»</button>
                </span>
            )}
        </div>
    );

    const unimputed = props.PaymentsReceived.filter((payment) => (payment.pendingIndictmentAmount ?? 0) > 0).length
        + props.PaymentsDone.filter((payment) => (payment.pendingIndictmentAmount ?? 0) > 0).length;

    const tabs: { key: WorkTab; label: string; count: number; warn?: boolean }[] = [
        { key: 'receivable', label: 'Por cobrar', count: rows.receivable.length },
        { key: 'payable', label: 'Por pagar', count: rows.payable.length },
        { key: 'received', label: 'Cobros', count: rows.received.length, warn: unimputed > 0 },
        { key: 'done', label: 'Pagos', count: rows.done.length },
        { key: 'settled', label: 'Saldadas', count: rows.settled.length }
    ];

    // ── Acciones ──
    // Un solo botón "Nuevo" con las cuatro cargas, sin importar la solapa.
    const NEW_OPTIONS: { key: WorkTab; label: string; hint: string; icon: JSX.Element; open: () => void }[] = [
        { key: 'receivable', label: 'Deuda a cobrar', hint: 'Alguien nos debe', icon: <ArrowDownLeft20Regular />, open: () => setDebtForm({ open: true, type: DebtFormType.Creditor }) },
        { key: 'payable', label: 'Deuda a pagar', hint: 'Le debemos a alguien', icon: <ArrowUpRight20Regular />, open: () => setDebtForm({ open: true, type: DebtFormType.Debtor }) },
        { key: 'received', label: 'Cobro', hint: 'Entró dinero', icon: <ReceiptMoney20Regular />, open: () => setPaymentForm({ open: true, type: PaymentFormType.receiver }) },
        { key: 'done', label: 'Pago', hint: 'Salió dinero', icon: <Wallet20Regular />, open: () => setPaymentForm({ open: true, type: PaymentFormType.payer }) }
    ];

    const editDebt = (debt: Debt) =>
        setDebtForm({ open: true, type: tab === 'payable' ? DebtFormType.Debtor : DebtFormType.Creditor, debtId: debt.id });
    const editPayment = (payment: Payment) =>
        setPaymentForm({ open: true, type: tab === 'done' ? PaymentFormType.payer : PaymentFormType.receiver, paymentId: payment.id });
    // Cobrar (o pagar) una deuda puntual: el formulario arranca con todo cargado.
    const settleDebt = (debt: Debt) =>
        setPaymentForm({ open: true, type: tab === 'payable' ? PaymentFormType.payer : PaymentFormType.receiver, debtId: debt.id });

    // Cobrar, pagar, imputar o deshacer cambia saldos en el servidor: al cerrar
    // cualquier drawer se vuelven a pedir las cuatro listas.
    const reloadAll = () => {
        props.GetAllDebtsByCreditor(props.EntityIdentifier, props.FixedAssociatedEntityIdentifier);
        props.GetAllDebtsByDebtor(props.EntityIdentifier, props.FixedAssociatedEntityIdentifier);
        props.GetAllPaymentsByPayer(props.EntityIdentifier, props.FixedAssociatedEntityIdentifier);
        props.GetAllPaymentsByReceiver(props.EntityIdentifier, props.FixedAssociatedEntityIdentifier);
    };

    const exportExcel = async () => {
        setExportState('busy');
        try {
            await downloadCashflow();
            setExportState('idle');
        } catch (error) {
            console.error('No se pudo generar el Excel del flujo de caja', error);
            setExportState('failed');
        }
    };

    const downloadCashflow = async () => {
        const sheets = cashflowSheets({
            kpis: currencies.map((code) => buildKpis({
                debtsAsCreditor: props.DebtsAsCreditor,
                debtsAsDebtor: props.DebtsAsDebtor,
                paymentsReceived: props.PaymentsReceived,
                paymentsDone: props.PaymentsDone,
                currency: code,
                range
            })),
            debtsAsCreditor: props.DebtsAsCreditor,
            debtsAsDebtor: props.DebtsAsDebtor,
            paymentsReceived: props.PaymentsReceived,
            paymentsDone: props.PaymentsDone,
            nameOf: props.NameOf
        });
        // El nombre lleva la extensión: SheetJS decide el formato por ella.
        await downloadWorkbook(buildFileName('flujo-de-caja'), sheets, {
            title: 'Flujo de caja',
            scope: `${fmtDate(range.from)} – ${fmtDate(range.to)}`,
            note: 'Por cobrar y por pagar son a la fecha; cobros y pagos, los del período.'
        });
    };

    const loading = props.isLoadingDebts || props.isLoadingPayments;
    const failed = props.failOnLoadingDebts || props.failOnLoadingPayments;
    const isFixedEntity = !!props.FixedAssociatedEntityIdentifier;

    return (
        <div className="cf-page">

            {/* ── Período y moneda ── */}
            <div className="cf-toolbar">
                <div className="cf-segmented" role="tablist" aria-label="Período">
                    {PRESETS.map((item) => (
                        <button
                            key={item.key}
                            type="button"
                            role="tab"
                            aria-selected={preset === item.key}
                            className={`cf-segmented__btn${preset === item.key ? ' is-active' : ''}`}
                            onClick={() => setPreset(item.key)}
                        >
                            {item.label}
                        </button>
                    ))}
                </div>
                {preset === 'custom' ? (
                    <span className="cf-toolbar__range">
                        <DatePicker
                            styles={{ root: { width: 150 } }}
                            ariaLabel="Desde"
                            value={customRange.from}
                            maxDate={customRange.to}
                            formatDate={fmtDate}
                            onSelectDate={(date) => date && setCustomRange((current) => ({ ...current, from: date }))}
                        />
                        –
                        <DatePicker
                            styles={{ root: { width: 150 } }}
                            ariaLabel="Hasta"
                            value={customRange.to}
                            minDate={customRange.from}
                            formatDate={fmtDate}
                            onSelectDate={(date) => date && setCustomRange((current) => ({ ...current, to: date }))}
                        />
                    </span>
                ) : (
                    <span className="cf-toolbar__range">{fmtDate(range.from)} – {fmtDate(range.to)}</span>
                )}

                <span className="cf-toolbar__spacer" />

                {currencies.length > 1 && (
                    <>
                        <span className="cf-toolbar__label">Moneda</span>
                        <div className="cf-segmented" role="tablist" aria-label="Moneda">
                            {currencies.map((code) => (
                                <button
                                    key={code}
                                    type="button"
                                    role="tab"
                                    aria-selected={currency === code}
                                    className={`cf-segmented__btn${currency === code ? ' is-active' : ''}`}
                                    onClick={() => setChosenCurrency(code)}
                                >
                                    {code}
                                </button>
                            ))}
                        </div>
                    </>
                )}
                <button type="button" className="cf-btn" onClick={exportExcel} disabled={loading || exportState === 'busy'}>
                    <ArrowDownload20Regular /> {exportState === 'busy' ? 'Preparando…' : 'Exportar a Excel'}
                </button>
                {exportState === 'failed' && (
                    <span className="cf-toolbar__error" role="alert">No se pudo generar el Excel.</span>
                )}
            </div>

            {failed && (
                <div className="cf-banner">
                    <strong>No se pudo cargar la caja.</strong>
                    <span>{props.errorDebts?.ErrorMessage || props.errorPayments?.ErrorMessage || 'Probá recargar la página.'}</span>
                </div>
            )}

            {/* ── Cómo estoy ── */}
            <div className="cf-kpis">
                <div className="cf-card">
                    <div className="cf-kpi__label">Por cobrar</div>
                    <div className="cf-kpi__value cf-kpi__value--receivable cf-num">{fmt(kpis.receivable, currency)}</div>
                    <div className="cf-kpi__hint">
                        {plural(kpis.receivableCount, 'deuda', 'deudas')}
                        {kpis.receivableOverdue > 0 && <> · <strong>{fmt(kpis.receivableOverdue, currency)}</strong> con más de 90 días</>}
                    </div>
                </div>
                <div className="cf-card">
                    <div className="cf-kpi__label">Cobrado en el período</div>
                    <div className="cf-kpi__value cf-num">{fmt(kpis.collectedInPeriod, currency)}</div>
                    <div className="cf-kpi__hint">
                        {kpis.collectedCount > 0 ? plural(kpis.collectedCount, 'cobro', 'cobros') : 'Sin cobros en el período'}
                        {unimputed > 0 && <> · <strong>{plural(unimputed, 'movimiento', 'movimientos')}</strong> sin imputar</>}
                    </div>
                </div>
                <div className="cf-card">
                    <div className="cf-kpi__label">Por pagar</div>
                    <div className="cf-kpi__value cf-num">{fmt(kpis.payable, currency)}</div>
                    <div className="cf-kpi__hint">
                        {kpis.payableCount > 0 ? plural(kpis.payableCount, 'deuda', 'deudas') : 'Sin pagos pendientes'}
                        {kpis.paidInPeriod > 0 && <> · {fmt(kpis.paidInPeriod, currency)} pagados en el período</>}
                    </div>
                </div>
                <div className="cf-card">
                    <div className="cf-kpi__label">Saldo neto proyectado</div>
                    <div className={`cf-kpi__value cf-num ${kpis.net < 0 ? 'cf-kpi__value--negative' : 'cf-kpi__value--net'}`}>{fmt(kpis.net, currency)}</div>
                    <div className="cf-kpi__hint">Por cobrar − por pagar</div>
                </div>
            </div>

            {/* ── Qué pasa en el tiempo ── */}
            <div className="cf-charts">
                <MonthlyChart points={monthly} currency={currency} />
                <AgingCard slices={aging} currency={currency} />
            </div>

            {/* ── Con quién ── */}
            {!isFixedEntity && (
                <div>
                    <div className="cf-section__head">
                        <h3 className="cf-section__title">Pendiente por contraparte</h3>
                        <div className="cf-chart-toggle" role="tablist" aria-label="Sentido">
                            <button type="button" role="tab" aria-selected={partiesMode === 'receivable'} className={`cf-chart-toggle__btn${partiesMode === 'receivable' ? ' is-active' : ''}`} onClick={() => setPartiesMode('receivable')}>Por cobrar</button>
                            <button type="button" role="tab" aria-selected={partiesMode === 'payable'} className={`cf-chart-toggle__btn${partiesMode === 'payable' ? ' is-active' : ''}`} onClick={() => setPartiesMode('payable')}>Por pagar</button>
                        </div>
                        <span className="cf-section__hint">Click para filtrar la mesa de trabajo</span>
                    </div>
                    {parties.length === 0 && (
                        <div className="cf-section__hint" style={{ marginTop: 8 }}>
                            {partiesMode === 'receivable' ? 'No hay nada pendiente de cobro' : 'No hay nada pendiente de pago'} en {currency}.
                        </div>
                    )}
                    <div className="cf-parties">
                        {parties.map((party) => (
                            <button
                                key={party.identifier}
                                type="button"
                                className={`cf-card cf-party${counterpartFilter === party.identifier ? ' is-active' : ''}`}
                                onClick={() => pickParty(party.identifier)}
                            >
                                <div className="cf-party__name" title={party.name}>{party.name}</div>
                                <div className="cf-party__amount cf-num">{fmt(party.amount, currency)}</div>
                                <div className={`cf-party__meta${party.oldestDays > 90 ? ' cf-party__meta--old' : ''}`}>
                                    {plural(party.count, 'deuda', 'deudas')} · la más antigua {party.oldestDays} días
                                </div>
                            </button>
                        ))}
                    </div>
                    {(hiddenParties > 0 || showAllParties) && allParties.length > PARTIES_PREVIEW && (
                        <div className="cf-parties__more">
                            <button type="button" className="cf-btn cf-btn--text" onClick={() => setShowAllParties((current) => !current)}>
                                {showAllParties ? 'Ver menos' : `Ver las ${allParties.length} contrapartes`}
                            </button>
                        </div>
                    )}
                </div>
            )}

            {/* ── Mesa de trabajo ── */}
            <div className="cf-card cf-work">
                <div className="cf-work__bar">
                    {tabs.map((item) => (
                        <button
                            key={item.key}
                            type="button"
                            className={`cf-pill${tab === item.key ? ' is-active' : ''}`}
                            onClick={() => setTab(item.key)}
                        >
                            {item.label}
                            <span className={`cf-pill__count${item.warn && tab !== item.key ? ' cf-pill__count--warn' : ''}`}>{item.count}</span>
                        </button>
                    ))}
                    {counterpartFilter && (
                        <span className="cf-work__filter">
                            <span className="cf-avatar" aria-hidden>{initialsOf(props.NameOf(counterpartFilter))}</span>
                            {props.NameOf(counterpartFilter)}
                            <button type="button" aria-label="Quitar filtro" title="Quitar filtro" onClick={() => setCounterpartFilter(undefined)}>×</button>
                        </span>
                    )}
                    <span className="cf-toolbar__spacer" />
                    <Menu positioning="below-end">
                        <MenuTrigger disableButtonEnhancement>
                            <button type="button" className="cf-btn cf-btn--primary" aria-label="Nuevo movimiento o deuda">
                                <Add20Regular /> Nuevo <ChevronDown16Regular />
                            </button>
                        </MenuTrigger>
                        <MenuPopover className="cf-menu">
                            <MenuList>
                                {NEW_OPTIONS.map((option) => (
                                    <MenuItem key={option.key} icon={option.icon} onClick={option.open}>
                                        <span className="cf-menu__label">{option.label}</span>
                                        <span className="cf-menu__hint">{option.hint}</span>
                                    </MenuItem>
                                ))}
                            </MenuList>
                        </MenuPopover>
                    </Menu>
                </div>

                {totalRows > 0 && pager}
                {(tab === 'receivable' || tab === 'payable' || tab === 'settled') && (
                    <DebtsTable
                        debts={pageSlice(rows[tab], currentPage, pageSize)}
                        allDebts={rows[tab]}
                        kind={tab === 'payable' ? 'payable' : 'receivable'}
                        settledView={tab === 'settled'}
                        entityIdentifier={props.EntityIdentifier}
                        isLoading={props.isLoadingDebts}
                        nameOf={props.NameOf}
                        onView={(debt) => setDetailsDebtId(debt.id)}
                        onEdit={editDebt}
                        onSettle={settleDebt}
                        onDelete={(debt) => props.DeleteDebt(debt.id)}
                        isDeleting={props.isDeletingDebt}
                        onCheckStatus={(debt) => props.CheckDebtStatus(debt.id)}
                        isCheckingStatus={props.isCheckingDebtStatus}
                        checkedStatusId={props.checkedDebtStatusId}
                        checkStatusResult={props.debtStatusResult}
                        checkStatusDone={props.successCheckingDebtStatus}
                    />
                )}
                {(tab === 'received' || tab === 'done') && (
                    <PaymentsTable
                        payments={pageSlice(rows[tab], currentPage, pageSize)}
                        allPayments={rows[tab]}
                        kind={tab}
                        isLoading={props.isLoadingPayments}
                        nameOf={props.NameOf}
                        onViewImputations={setImputationsOf}
                        onImpute={setImputing}
                        onEdit={editPayment}
                        onDelete={(payment) => props.DeletePayment(payment.id)}
                        isDeleting={props.isDeletingPayment}
                    />
                )}
            </div>

            {/* ── Paneles ── */}
            <DebtFormComponent
                FormType={debtForm.type}
                ReferalId={props.EntityIdentifier}
                FixedAssociatedEntityIdentifier={props.FixedAssociatedEntityIdentifier}
                isOpen={debtForm.open}
                DebtId={debtForm.debtId}
                PrefillCounterpartId={counterpartFilter}
                onClose={() => { setDebtForm((current) => ({ ...current, open: false, debtId: undefined })); reloadAll(); }}
            />
            <DebtDetailsComponent
                DebtId={detailsDebtId}
                isOpen={detailsDebtId !== undefined}
                onClose={() => setDetailsDebtId(undefined)}
            />
            <PaymentFormComponent
                FormType={paymentForm.type}
                ReferalId={props.EntityIdentifier}
                FixedAssociatedEntityIdentifier={props.FixedAssociatedEntityIdentifier}
                PaymentId={paymentForm.paymentId}
                PrefillDebtId={paymentForm.debtId}
                PrefillCounterpartId={counterpartFilter}
                isOpen={paymentForm.open}
                onClose={() => { setPaymentForm((current) => ({ ...current, open: false, paymentId: undefined, debtId: undefined })); reloadAll(); }}
            />
            <PaymentImputationsViewComponent
                paymentId={imputationsOf?.id ?? null}
                isOpen={imputationsOf !== null}
                onClose={() => { setImputationsOf(null); reloadAll(); }}
                title={tab === 'done' ? 'Imputaciones del pago' : 'Imputaciones del cobro'}
            />
            <PaymentImputationFormComponent
                payment={imputing}
                isOpen={imputing !== null}
                onClose={() => { setImputing(null); reloadAll(); }}
                title="Imputar saldo pendiente"
            />
        </div>
    );
};

export default connect(
    ownFunctions.mapStateToProps,
    ownFunctions.mapDispatchToProps
)(CashFlowGeneralComponent as any);
